import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Input, Box, Flex, Icon } from '@chakra-ui/react';
import { useField } from 'formik';
import { colors } from '@/theme/foundations/colors';
import { KText } from '@/ui-library/components';
import { XClose } from '@kula-ai/icons/outline/XClose';

export const TagInput = ({ item = {}, field, formik }) => {
  const {
    name,
    placeholder = 'Use Space or Enter for multiple keywords',
    styles,
    disabled,
    expanded = true,
    toggleExpandCollapse = () => {},
  } = item;

  const [, , helpers] = useField(name);
  const { setValue } = helpers || {};
  const [inputValue, setInputValue] = useState('');
  const [showAll, setShowAll] = useState(expanded);
  const [maxVisibleTags, setMaxVisibleTags] = useState(3);
  const inputRef = useRef();

  const tags = useMemo(() => {
    return Array.isArray(field?.value) ? field.value : [];
  }, [field?.value]);

  // Update showAll when expanded prop changes
  useMemo(() => {
    setShowAll(expanded);
  }, [expanded]);

  // Simple approach: calculate based on average tag length
  const calculateMaxVisibleTags = useCallback(() => {
    if (tags.length === 0) return 2;

    // Calculate average tag length
    const avgTagLength =
      tags.reduce((sum, tag) => sum + tag.length, 0) / tags.length;

    // Rough estimation: longer tags = show fewer, shorter tags = show more
    if (avgTagLength <= 3) return Math.min(tags.length, 4); // Short tags like "js", "go"
    if (avgTagLength <= 6) return Math.min(tags.length, 3); // Medium tags like "react", "python"
    if (avgTagLength <= 10) return Math.min(tags.length, 2); // Longer tags
    return Math.min(tags.length, 1); // Very long tags
  }, [tags]);

  // Update maxVisibleTags when tags change
  useEffect(() => {
    setMaxVisibleTags(calculateMaxVisibleTags());
  }, [tags, calculateMaxVisibleTags]);

  // Function to clean and split pasted values into tags
  const processValue = (value) => {
    if (!value || Array.isArray(value)) {
      return value;
    }

    // If it's a string, split by comma and space, clean up, and filter out empty strings
    const cleanedTags = value
      .split(/[,\s]+/) // Split by comma and/or spaces
      .map((tag) => tag.trim()) // Trim whitespace
      .filter((tag) => tag.length > 0); // Remove empty strings

    return cleanedTags;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        const newTag = inputValue.trim();
        if (!tags.includes(newTag)) {
          const newTags = [...tags, newTag];
          if (setValue) {
            setValue(newTags);
          }
          if (formik?.setFieldValue) {
            formik.setFieldValue(name, newTags);
          }
        }
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      e.preventDefault();
      const newTags = tags.slice(0, -1);
      if (setValue) {
        setValue(newTags);
      }
      if (formik?.setFieldValue) {
        formik.setFieldValue(name, newTags);
      }
    }
  };

  const handleRemoveTag = (e, tagToRemove) => {
    e.preventDefault();
    e.stopPropagation();
    const newTags = tags.filter((tag) => tag !== tagToRemove);
    if (setValue) {
      setValue(newTags);
    }
    if (formik?.setFieldValue) {
      formik.setFieldValue(name, newTags);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    if (inputValue.trim() && !tags.includes(inputValue.trim())) {
      const newTags = [...tags, inputValue.trim()];
      if (setValue) {
        setValue(newTags);
      }
      if (formik?.setFieldValue) {
        formik.setFieldValue(name, newTags);
      }
    }
    setInputValue('');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    if (pastedText.trim()) {
      const processedTags = processValue(pastedText);

      if (processedTags.length > 0) {
        // Merge with existing tags, avoiding duplicates
        const newTags = [...new Set([...tags, ...processedTags])];
        if (setValue) {
          setValue(newTags);
        }
        if (formik?.setFieldValue) {
          formik.setFieldValue(name, newTags);
        }
      }
    }
  };

  const shouldShowToggle = tags.length > maxVisibleTags;
  const displayTags = showAll ? tags : tags.slice(0, maxVisibleTags);
  const hiddenCount = tags.length - maxVisibleTags;

  const handleToggleShowAll = () => {
    setShowAll((prev) => !prev);
    toggleExpandCollapse();
  };

  return (
    <Flex
      w="100%"
      border="1px solid"
      borderColor={colors['border-color']}
      borderRadius="6px"
      minH="40px"
      p="4px 12px"
      flexWrap="wrap"
      alignItems="center"
      gap="4px"
      bg="white"
    >
      <Flex flexWrap="wrap" alignItems="center" gap="4px">
        {displayTags.map((tag, index) => (
          <Flex
            key={index}
            alignItems="center"
            gap="4px"
            border="1px solid"
            borderColor="border-color"
            borderRadius="6px"
            p="2px 6px"
          >
            <KText mt="-4px">{tag}</KText>
            <Box cursor="pointer" onClick={(e) => handleRemoveTag(e, tag)}>
              <Icon as={XClose} w="14px" h="14px" />
            </Box>
          </Flex>
        ))}

        {!showAll && shouldShowToggle && (
          <Flex
            cursor="pointer"
            onClick={handleToggleShowAll}
            border="1px solid"
            borderColor={colors['border-color']}
            borderRadius="6px"
            p="3px 6px"
            alignItems="center"
            justifyContent="center"
          >
            <KText color="primary-500">+{hiddenCount}</KText>
          </Flex>
        )}
      </Flex>

      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        variant="unstyled"
        autoComplete="off"
        flex="1"
        minW="120px"
        placeholder={tags.length === 0 ? placeholder : ''}
        disabled={disabled}
        {...styles}
      />
    </Flex>
  );
};
